import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CartContext } from '../../App';
import { useAuthStore } from '../../store/auth';
import apiInstance from '../../utils/axios';
import Swal from 'sweetalert2';
import { Helmet } from 'react-helmet-async';
import GetCurrentAddress from '../plugin/UserCountry';
import getCartID from '../plugin/cartID';
import UserData from '../plugin/UserData';
import moment from 'moment';
import ProductVariant_01 from '@/components/commerce-ui/product-variants-01';
import QuantityInputBasic from '@/components/commerce-ui/quantity-input-basic';
import PriceFormat_Basic from '@/components/commerce-ui/price-format-basic';
import VariantSelectorImages from '@/components/ui/VariantSelectorImages';


function ProductDetail() {
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [specifications, setSpecifications] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [color, setColor] = useState([]);
    const [size, setSize] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [selectedSize, setSelectedSize] = useState(''); // Don't call t() in initial state
    const [selectedColor, setSelectedColor] = useState(''); // Don't call t() in initial state
    const [colorImage, setColorImage] = useState("");
    const [colorGallery, setColorGallery] = useState([]);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [translatedRelatedProducts, setTranslatedRelatedProducts] = useState([]);
    const [currentImage, setCurrentImage] = useState("");
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const [displayedGallery, setDisplayedGallery] = useState([]);
    const [currentPrice, setCurrentPrice] = useState(0);
    const [priceLoading, setPriceLoading] = useState(false);
    

    const [cartCount, updateCartCountAndNotify] = useContext(CartContext);


    const param = useParams();
    const currentAddress = GetCurrentAddress();
    const userData = UserData();
    let cart_id = getCartID();
    const slug = param.slug;
    const urlColor = param.color;
    const urlSize = param.size;
    const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

    const Toast = Swal.mixin({
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    // Dynamic label for size/capacity on frontend
    const sizeLabel = product?.size_label || 'Tamaño';

    // Helper function to create gallery like Amazon - main product image + color-specific images
    const createUniqueGallery = (mainProductImage, colorImages, productGalleryImages) => {
        const uniqueImages = [];
        const seenImages = new Set();
        
        // 1. Always add main product image first (the product itself)
        if (mainProductImage) {
            uniqueImages.push({ image: mainProductImage, isMain: true, type: 'product' });
            seenImages.add(mainProductImage);
        }
        
        // 2. Add color-specific images (different from main product image)
        if (colorImages && Array.isArray(colorImages)) {
            colorImages.forEach(img => {
                const imageUrl = typeof img === 'string' ? img : img.image;
                if (imageUrl && !seenImages.has(imageUrl)) {
                    uniqueImages.push({ image: imageUrl, isMain: false, type: 'color' });
                    seenImages.add(imageUrl);
                }
            });
        }
        
        // 3. Add additional product gallery images (if any)
        if (productGalleryImages && Array.isArray(productGalleryImages)) {
            productGalleryImages.forEach(img => {
                const imageUrl = typeof img === 'string' ? img : img.image;
                if (imageUrl && !seenImages.has(imageUrl)) {
                    uniqueImages.push({ image: imageUrl, isMain: false, type: 'gallery' });
                    seenImages.add(imageUrl);
                }
            });
        }
        
        return uniqueImages;
    };

    // Function to get current price based on selected variants
    const getCurrentPrice = () => {
        if (!product?.variants || product.variants.length === 0) {
            return product?.price || 0;
        }

        // Find matching variant
        const variant = product.variants.find(v => {
            const colorMatch = (!selectedColor || selectedColor === 'No Color') 
                ? (!v.color_name || v.color_name === 'No Color')
                : v.color_name === selectedColor;
            
            const sizeMatch = (!selectedSize || selectedSize === 'No Size')
                ? (!v.size_name || v.size_name === 'No Size') 
                : v.size_name === selectedSize;

            return colorMatch && sizeMatch;
        });

        return variant?.price || product?.price || 0;
    };

    

    // Function to check if a color-size combination is available
    const isVariantAvailable = (colorName, sizeName) => {
        if (!product?.variants || product.variants.length === 0) {
            return true; // If no variants, assume available
        }

        // Handle undefined values
        if (colorName === undefined || sizeName === undefined) {
            return true; // If values are undefined, assume available
        }

        const variant = product.variants.find(v => {
            const colorMatch = (!colorName || colorName === 'No Color') 
                ? (!v.color_name || v.color_name === 'No Color')
                : v.color_name === colorName;
            
            const sizeMatch = (!sizeName || sizeName === 'No Size')
                ? (!v.size_name || v.size_name === 'No Size') 
                : v.size_name === sizeName;

            return colorMatch && sizeMatch;
        });

        return variant && variant.stock_qty > 0;
    };

    // Function to check if a color has any available sizes
    const isColorAvailable = (colorName) => {
        if (!product?.variants || product.variants.length === 0) {
            return true;
        }

        // Check if this color has any variants with stock
        return product.variants.some(v => {
            const colorMatch = (!colorName || colorName === 'No Color') 
                ? (!v.color_name || v.color_name === 'No Color')
                : v.color_name === colorName;
            
            return colorMatch && v.stock_qty > 0;
        });
    };

    // Function to check if a size has any available colors
    const isSizeAvailable = (sizeName) => {
        if (!product?.variants || product.variants.length === 0) {
            return true;
        }

        // Check if this size has any variants with stock
        return product.variants.some(v => {
            const sizeMatch = (!sizeName || sizeName === 'No Size')
                ? (!v.size_name || v.size_name === 'No Size') 
                : v.size_name === sizeName;
            
            return sizeMatch && v.stock_qty > 0;
        });
    };

    // Function to check if current selection is available
    const isCurrentSelectionAvailable = () => {
        return isVariantAvailable(selectedColor, selectedSize);
    };

    // Memoized current price calculation for better performance
    const currentPriceValue = React.useMemo(() => {
        return getCurrentPrice();
    }, [selectedColor, selectedSize, product]);

    // Update current price when variants change
    useEffect(() => {
        if (product) {
            setPriceLoading(true);
            // Simulate loading delay for metallic effect
            const timer = setTimeout(() => {
                setCurrentPrice(currentPriceValue);
                setPriceLoading(false);
            }, 300);
            
            return () => clearTimeout(timer);
        }
    }, [currentPriceValue, product]);

    // Initialize price when product loads
    useEffect(() => {
        if (product) {
            setCurrentPrice(currentPriceValue);
        }
    }, [product, currentPriceValue]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]); // Scroll to top when product changes

    useEffect(() => {
        const fetchProductData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Reset all product-specific state to prevent showing old data
                setProduct(null);
                setSpecifications([]);
                setGallery([]);
                setColor([]);
                setSize([]);
                setSelectedSize('');
                setSelectedColor('');
                setColorImage("");
                setColorGallery([]);
                setRelatedProducts([]);
                setTranslatedRelatedProducts([]);
                setCurrentImage("");
                setDisplayedGallery([]);
                setCurrentPrice(0);
                
                // Build API URL based on whether color and size are provided
                let apiUrl = `/products/${slug}`;
                if (urlColor && urlSize) {
                    apiUrl = `/products/${slug}/${encodeURIComponent(urlColor)}/${encodeURIComponent(urlSize)}`;
                }
                
                const res = await apiInstance.get(apiUrl);
                
                if (!res.data) {
                    throw new Error('Product not found');
                }
                
                // Store original product data
                setProduct(res.data);
                
                setSpecifications(res.data.specification || []);
                setGallery(res.data.gallery || []);
                
                setColor(res.data.color || []);
                setSize(res.data.size || []);
                setCurrentImage(res.data.image);
                
                // Initialize displayed gallery with only the first color's images
                if (res.data.color?.length > 0) {
                    const firstColor = res.data.color[0];
                    setDisplayedGallery(createUniqueGallery(firstColor.image, firstColor.galleries || [], []));
                } else {
                setDisplayedGallery(createUniqueGallery(res.data.image, [], res.data.gallery));
                }
                
                // Handle size selection from URL or default
                if (res.data.size?.length > 0) {
                    if (urlSize && res.data.size.some(s => s.name === urlSize)) {
                        setSelectedSize(urlSize);
                    } else {
                        setSelectedSize(res.data.size[0].name);
                    }
                } else {
                    setSelectedSize('No Size');
                }
                
                // Handle color selection from URL or default
                if (res.data.color?.length > 0) {
                    if (urlColor && res.data.color.some(c => c.name === urlColor)) {
                        setSelectedColor(urlColor);
                        const selectedColorObj = res.data.color.find(c => c.name === urlColor);
                        setColorImage(selectedColorObj.image);
                        setColorGallery(selectedColorObj.galleries || []);
                    } else {
                        // Always select the first color by default
                        const firstColor = res.data.color[0];
                        setSelectedColor(firstColor.name);
                        setColorImage(firstColor.image);
                        setColorGallery(firstColor.galleries || []);
                        setCurrentImage(firstColor.image); // Set as main image too
                    }
                } else {
                    setSelectedColor('No Color');
                }
                
                setLoading(false);
            } catch (err) {
                console.error('Error fetching product:', err);
                setError(err.response?.data?.detail || err.message || 'Producto no encontrado');
                setProduct(null);
                setLoading(false);
            }
        };

        fetchProductData();
    }, [slug]); // Removed translation dependencies

    // Server-provided translations are applied from API response

    const navigate = useNavigate();
    
    // Function to update URL with color and size parameters (without page refresh)
    const updateProductURL = (colorName, sizeName) => {
        const encodedColor = encodeURIComponent(colorName);
        const encodedSize = encodeURIComponent(sizeName);
        const newPath = `/detail/${slug}/${encodedColor}/${encodedSize}/`;
        
        // Update URL without page refresh using browser history API
        // Use replaceState instead of pushState to avoid creating history entries
        window.history.replaceState({}, '', newPath);
    };

    // Memoized color button click handler for better performance
    const handleColorButtonClick = React.useCallback((product_id, colorName, colorImage, colorGalleries) => {
        setSelectedColor(colorName);
        setColorImage(colorImage);
        setColorGallery(colorGalleries);
        setCurrentImage(colorImage);
        setIsZoomed(false);
        
        // Update displayed gallery when color changes - only color images for selected color
        setDisplayedGallery(createUniqueGallery(colorImage, colorGalleries, []));
        
        // Update URL with new color and size
        updateProductURL(colorName, selectedSize);
    }, [selectedSize, updateProductURL]);

    // Memoized size button click handler for better performance
    const handleSizeButtonClick = React.useCallback((sizeName) => {
        setSelectedSize(sizeName);
        
        // Update URL with new color and size
        updateProductURL(selectedColor, sizeName);
    }, [selectedColor, updateProductURL]);

    // Memoized thumbnail click handler for better performance
    const handleThumbnailClick = React.useCallback((image, index) => {
        // Find the clicked image in the displayed gallery
        const clickedImage = displayedGallery.find(img => img.image === image);
        
        // If the clicked image is not already the current image
        if (currentImage !== image) {
            // If the clicked image was the main product image (isMain: true)
            if (clickedImage?.isMain) {
                // Set the current image to the main product image
                setCurrentImage(image);
            } else {
                // For other gallery images, set them as current image
                setCurrentImage(image);
            }
        }
    }, [displayedGallery, currentImage]);


    // Memoized mouse event handlers for better performance
    const handleMouseMove = React.useCallback((e) => {
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = ((e.pageX - left) / width) * 100;
        const y = ((e.pageY - top) / height) * 100;
        setZoomPosition({ x, y });
    }, []);

    const handleMouseEnter = React.useCallback(() => {
        setIsZoomed(true);
    }, []);

    const handleMouseLeave = React.useCallback(() => {
        setIsZoomed(false);
    }, []);

    // Memoized add to cart handler for better performance
    const handleAddToCart = React.useCallback(async () => {
        if (!product) return;

        // Allow both logged-in and guest users to add to cart
        // Guest users can still add items, they just won't be associated with a user account

        // Check product stock
        if (product.stock_qty <= 0) {
            Swal.fire({
                icon: 'error',
                            title: 'Sin Stock',
            text: 'Este producto está actualmente agotado.',
            });
            return;
        }

        // Check if requested quantity exceeds available stock
        if (quantity > product.stock_qty) {
            Swal.fire({
                icon: 'error',
                title: 'Stock Insuficiente',
                text: `Solo ${product.stock_qty} disponible en stock. Solicitaste ${quantity}.`,
            });
            return;
        }

        // Check if requested quantity exceeds max cart limit
        if (quantity > product.max_cart_limit) {
            Swal.fire({
                icon: 'error',
                title: 'Límite del Carrito Excedido',
                text: `Solo puedes agregar hasta ${product.max_cart_limit} artículos de este producto a tu carrito.`,
            });
            return;
        }

        // Validate color selection if product has colors
        if (color.length > 0) {
            if (selectedColor === 'No Color') {
                Swal.fire({
                    icon: 'error',
                                    title: 'Color Requerido',
                text: 'Por favor selecciona un color antes de agregar al carrito.',
                });
                return;
            }
            
            // Check selected color stock
            const selectedColorObj = color.find(c => c.name === selectedColor);
            if (selectedColorObj) {
                if (selectedColorObj.stock_qty <= 0) {
                    Swal.fire({
                        icon: 'error',
                                    title: 'Color Agotado',
            text: 'El color seleccionado está actualmente agotado.',
                    });
                    return;
                }
                
                if (quantity > selectedColorObj.stock_qty) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Stock Insuficiente de Color',
                        text: `Solo ${selectedColorObj.stock_qty} disponible en stock para el color '${selectedColor}'. Solicitaste ${quantity}.`,
                    });
                    return;
                }
            }
        }

        // Validate size selection if product has sizes
        if (size.length > 0) {
            if (selectedSize === 'No Size') {
                Swal.fire({
                    icon: 'error',
                                    title: 'Tamaño Requerido',
                text: 'Por favor selecciona un tamaño antes de agregar al carrito.',
                });
                return;
            }
            
            // Check selected size stock
            const selectedSizeObj = size.find(s => s.name === selectedSize);
            if (selectedSizeObj) {
                if (selectedSizeObj.stock_qty <= 0) {
                    Swal.fire({
                        icon: 'error',
                                    title: 'Tamaño Agotado',
            text: 'El tamaño seleccionado está actualmente agotado.',
                    });
                    return;
                }
                
                if (quantity > selectedSizeObj.stock_qty) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Stock Insuficiente de Tamaño',
                        text: `Solo ${selectedSizeObj.stock_qty} disponible en stock para el tamaño '${selectedSize}'. Solicitaste ${quantity}.`,
                    });
                    return;
                }
            }
        }

        // Validate variant combination availability (color + size)
        if (product?.variants && product.variants.length > 0) {
            if (!isVariantAvailable(selectedColor, selectedSize)) {
                Swal.fire({
                    icon: 'info',
                    title: 'Selección No Disponible',
                    text: 'Esta combinación de color/talla está agotada. Por favor elija otra combinación.',
                });
                return;
            }
        }

        try {
            const formdata = new FormData();
            formdata.append('product_id', product.id);
            formdata.append('user_id', userData?.user_id);
            formdata.append('qty', quantity);
            formdata.append('price', currentPrice || product.price);
            formdata.append('shipping_ammount', product.shipping_ammount);
            formdata.append('country', currentAddress.country);
            formdata.append('size', selectedSize);
            formdata.append('color', selectedColor);
            formdata.append('cart_id', cart_id);

            await apiInstance.post('cart-view/', formdata);

            const url = userData ? `cart-list/${cart_id}/${userData?.user_id}/` : `cart-list/${cart_id}/`;
            const res = await apiInstance.get(url);
            const cartItemsCount = res.data.cart_items?.length || 0;
            
            // Update cart count and show notification
            updateCartCountAndNotify(cartItemsCount);

            Toast.fire({
                icon: 'success',
                title: 'Agregado al Carrito',
            });
        } catch (error) {
            
            // Handle specific error messages from backend
            let errorMessage = 'Error al agregar producto al carrito. Por favor intente nuevamente.';
            let errorTitle = 'Error';
            
            if (error.response && error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
                
                // Set appropriate title based on error type
                if (errorMessage.includes('stock') || errorMessage.includes('Stock')) {
                    errorTitle = 'Problema de Stock';
                } else if (errorMessage.includes('color') || errorMessage.includes('Color')) {
                    errorTitle = 'Selección de Color Requerida';
                } else if (errorMessage.includes('size') || errorMessage.includes('Size')) {
                    errorTitle = 'Selección de Talla Requerida';
                } else if (errorMessage.includes('limit') || errorMessage.includes('Limit')) {
                    errorTitle = 'Límite de Cantidad Excedido';
                }
            }
            
            Swal.fire({
                icon: 'error',
                title: errorTitle,
                text: errorMessage,
                confirmButtonText: 'OK'
            });
        }
    }, [product, quantity, color, selectedColor, size, selectedSize, currentPrice, userData, cart_id, currentAddress, updateCartCountAndNotify]);

    // Handler used by the new ProductVariant_01 component
    const handleAddToCartFromVariant = React.useCallback(async (payload) => {
        if (!product) return;

        // payload: { variantId, quantity, price, salePrice, totalPrice, variantLabel }
        const variantId = payload.variantId;
        const qty = payload.quantity || 1;

        // Find matching variant by sku/value or id
        const variant = (product.variants || []).find(v => {
            const vid = v.sku ?? v.value ?? v.id ?? `${v.id}`;
            return `${vid}` === `${variantId}` || `${v.id}` === `${variantId}`;
        }) || null;

        // Basic validations (mirror existing logic)
        const availableQty = variant?.stock_qty ?? variant?.availableQuantity ?? product.stock_qty ?? 0;

        if (availableQty <= 0) {
            Swal.fire({ icon: 'error', title: 'Agotado', text: 'Esta variante del producto está actualmente agotada.' });
            return;
        }

        if (qty > availableQty) {
            Swal.fire({ icon: 'error', title: 'Stock Insuficiente', text: `Solo ${availableQty} disponible en stock. Solicitaste ${qty}.` });
            return;
        }

        if (qty > product.max_cart_limit) {
            Swal.fire({ icon: 'error', title: 'Límite del Carrito Excedido', text: `Solo puedes agregar hasta ${product.max_cart_limit} artículos de este producto a tu carrito.` });
            return;
        }

        try {
            const formdata = new FormData();
            formdata.append('product_id', product.id);
            formdata.append('user_id', userData?.user_id);
            formdata.append('qty', qty);
            formdata.append('price', payload.price ?? payload.salePrice ?? currentPrice ?? product.price);
            formdata.append('shipping_ammount', product.shipping_ammount);
            formdata.append('country', currentAddress.country);
            // include size/color if available on the variant
            formdata.append('size', variant?.size_name ?? '');
            formdata.append('color', variant?.color_name ?? '');
            formdata.append('cart_id', cart_id);

            await apiInstance.post('cart-view/', formdata);

            const url = userData ? `cart-list/${cart_id}/${userData?.user_id}/` : `cart-list/${cart_id}/`;
            const res = await apiInstance.get(url);
            const cartItemsCount = res.data.cart_items?.length || 0;
            updateCartCountAndNotify(cartItemsCount);

            Toast.fire({ icon: 'success', title: 'Agregado al Carrito' });
            setTimeout(() => { window.location.href = '/'; }, 1500);
        } catch (error) {
            let errorMessage = 'Error al agregar producto al carrito. Por favor intente nuevamente.';
            if (error.response && error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
            }
            Swal.fire({ icon: 'error', title: 'Error', text: errorMessage, confirmButtonText: 'OK' });
        }
    }, [product, currentPrice, userData, cart_id, currentAddress, updateCartCountAndNotify]);


    useEffect(() => {
        if (product?.category?.id) {
            apiInstance.get(`products/?category=${product.category.id}`).then((res) => {
                // Handle both direct array and paginated response
                const productsData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                const filteredProducts = productsData.filter((p) => p.id !== product.id);
                setRelatedProducts(filteredProducts);
                
                // No translation needed - just use the products directly
                setTranslatedRelatedProducts(filteredProducts);
            });
        }
    }, [product]);


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger text-center m-5">
                {error}
                <Link to="/" className="btn btn-primary ms-3">Volver al Inicio</Link>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="alert alert-warning text-center m-5">
                Producto no encontrado
                <Link to="/" className="btn btn-primary ms-3">Volver al Inicio</Link>
            </div>
        );
    }

    return (
        <main className="mb-4 mt-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            <Helmet>
                <title>{product?.title} - Super Paraguai</title>
                <meta name="description" content={product?.description?.substring(0, 160)} />
                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            </Helmet>

            <style>{`
                .product-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 10px;
                }
                .product-description {
                    line-height: 1.6;
                    color: #4a5568;
                    font-size: 14px;
                    margin-bottom: 1rem;
                }
                .product-description p {
                    margin-bottom: 0.75rem;
                    white-space: pre-wrap;
                }
                .product-description p:last-child {
                    margin-bottom: 0;
                }
                .original-price {
                    color: #e53e3e;
                    font-size: 1.1rem;
                    text-decoration: line-through;
                    font-weight: 500;
                }
                .current-price {
                    color: #2ecc71;
                    font-size: 1.8rem;
                    font-weight: 700;
                    transition: all 0.3s ease;
                }
                
                .price-loading {
                    background: linear-gradient(90deg, 
                        transparent 0%, 
                        #2ecc71 25%, 
                        #27ae60 50%, 
                        #2ecc71 75%, 
                        transparent 100%
                    );
                    background-size: 300% 100%;
                    animation: metallic-shimmer 1.2s ease-out;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    position: relative;
                }
                
                @keyframes metallic-shimmer {
                    0% {
                        background-position: -300% 0;
                    }
                    100% {
                        background-position: 300% 0;
                    }
                }
                
                /* Clean Specifications Table */
                .specifications-table {
                    display: flex;
                    flex-direction: column;
                    gap: 0;
                }
                
                .spec-row {
                    display: flex;
                    padding: 16px 0;
                    border-bottom: 1px solid #f1f3f4;
                }
                
                .spec-row:last-child {
                    border-bottom: none;
                }
                
                .spec-label {
                    flex: 0 0 35%;
                    font-weight: 500;
                    color: #6c757d;
                    font-size: 15px;
                    padding-right: 20px;
                }
                
                .spec-value {
                    flex: 1;
                    color: #212529;
                    font-size: 15px;
                    font-weight: 400;
                    line-height: 1.4;
                }
                
                @media (max-width: 768px) {
                    .spec-row {
                        flex-direction: column;
                        padding: 12px 0;
                        gap: 4px;
                    }
                    
                    .spec-label {
                        flex: none;
                        font-size: 14px;
                        font-weight: 600;
                        color: #495057;
                        padding-right: 0;
                        margin-bottom: 4px;
                    }
                    
                    .spec-value {
                        flex: none;
                        font-size: 14px;
                        color: #212529;
                    }
                }
                .discount-badge {
                    background-color: #2ecc71;
                    color: white;
                    font-size: 0.85rem;
                    padding: 0.25rem 0.5rem;
                    border-radius: 4px;
                    margin-left: 0.5rem;
                }
                .image-container {
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    background: #fff;
                    padding: 10px;
                }
                .main-product-image {
                    width: 100%;
                    height: 400px;
                    object-fit: contain;
                    transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                .image-zoom-container {
                    position: relative;
                    overflow: hidden;
                    cursor: zoom-in;
                }
                .image-zoom-container.zoomed .main-product-image {
                    transform: scale(2);
                    transform-origin: ${zoomPosition.x}% ${zoomPosition.y}%;
                    cursor: zoom-out;
                }
                .thumbnails-container {
                    display: flex;
                    gap: 8px;
                    margin-top: 10px;
                    flex-wrap: wrap;
                }
                .thumbnail-image {
                    width: 50px;
                    height: 50px;
                    object-fit: contain;
                    border-radius: 6px;
                    cursor: pointer;
                    border: 2px solid transparent;
                    transition: all 0.2s ease;
                }
                .thumbnail-image:hover, .thumbnail-image.active {
                    border-color: #3498db;
                    transform: translateY(-3px);
                }
                .product-info-section {
                    background: #fff;
                    border-radius: 8px;
                    padding: 15px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    height: 100%;
                }
                .product-title {
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #2d3748;
                    margin-bottom: 0.4rem;
                    line-height: 1.3;
                }
                .price-container {
                    display: flex;
                    align-items: center;
                    margin-bottom: 1rem;
                }
                .specs-table {
                    width: 100%;
                    margin-bottom: 1rem;
                }
                .specs-table tr:not(:last-child) {
                    border-bottom: 1px solid #edf2f7;
                }
                .specs-table th {
                    padding: 0.5rem 0;
                    color: #4a5568;
                    font-weight: 500;
                    width: 30%;
                }
                .specs-table td {
                    padding: 0.5rem 0;
                    color: #2d3748;
                }
                .product-details-mobile {
                    display: none;
                }
                .specs-table {
                    display: table;
                }
                .detail-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.3rem 0;
                    font-size: 0.9rem;
                }
                .detail-label {
                    color: #6c757d;
                    font-weight: 500;
                }
                .detail-value {
                    color: #2d3748;
                    font-weight: 400;
                }
                .back-arrow-container {
                    display: flex;
                    align-items: center;
                }
                .back-arrow-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8f9fa;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 8px 16px;
                    color: #4a5568;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .back-arrow-btn:hover {
                    background: #e9ecef;
                    border-color: #cbd5e0;
                }
                .back-arrow-btn i {
                    font-size: 12px;
                }
                .variant-options {
                    margin-bottom: 0.8rem;
                }
                .variant-label {
                    display: block;
                    margin-bottom: 0.4rem;
                    color: #4a5568;
                    font-weight: 500;
                }
                .variant-buttons {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }
                .variant-btn {
                    padding: 6px 12px;
                    border: 1px solid #e2e8f0;
                    background: #ffffff;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 13px;
                    font-weight: 500;
                    margin-right: 6px;
                    margin-bottom: 6px;
                    min-width: 45px;
                    text-align: center;
                    position: relative;
                    color: #2d3748;
                    overflow: hidden; /* keep cross lines within borders */
                }
                .variant-btn:hover:not(.out-of-stock):not(.unavailable-combination) {
                    border-color: #3498db;
                    background: #f8f9fa;
                    color: #2d3748;
                }
                .variant-btn.selected {
                    border-color: #3498db;
                    background: #3498db;
                    color: white;
                }
                .variant-btn.out-of-stock,
                .variant-btn.unavailable-combination {
                    position: relative;
                    cursor: not-allowed;
                    background: inherit;
                    border-color: #e2e8f0;
                    color: #495057;
                    /* Subtle unavailable effect: slightly grey and blurred but still readable */
                    filter: grayscale(0.35) saturate(0.9) brightness(0.98) blur(0.25px);
                    box-shadow: inset 0 0 0 9999px rgba(90, 90, 90, 0.05);
                }
                .variant-btn.out-of-stock::before,
                .variant-btn.out-of-stock::after,
                .variant-btn.unavailable-combination::before,
                .variant-btn.unavailable-combination::after {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    width: calc(100% - 14px); /* tiny inset adjustment */
                    height: 1px;
                    background-color: rgba(90, 90, 90, 0.9);
                    transform-origin: center;
                    pointer-events: none;
                    z-index: 2;
                }
                .variant-btn.out-of-stock::before,
                .variant-btn.unavailable-combination::before { transform: translate(-50%, -50%) rotate(45deg); }
                .variant-btn.out-of-stock::after,
                .variant-btn.unavailable-combination::after { transform: translate(-50%, -50%) rotate(-45deg); }
                .out-of-stock-badge {
                    display: none; /* Hide the old X badge since we're using ::after */
                }
                .low-stock {
                    border-color: #f39c12 !important;
                    background-color: #fef9e7 !important;
                    color: #d68910 !important;
                }
                .low-stock-indicator {
                    position: absolute;
                    top: -3px;
                    right: -3px;
                    background: #f39c12;
                    color: white;
                    border-radius: 50%;
                    width: 12px;
                    height: 12px;
                    font-size: 8px;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                    z-index: 2;
                    pointer-events: none;
                }
                .color-swatch {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    cursor: pointer;
                    border: 2px solid transparent;
                    transition: all 0.2s ease;
                    position: relative;
                    margin-right: 6px;
                    margin-bottom: 6px;
                    display: inline-block;
                    overflow: hidden;
                }
                .color-swatch:hover:not(.out-of-stock) {
                    border-color: #3498db;
                    transform: scale(1.1);
                }
                .color-swatch.selected {
                    border-color: #3498db;
                    transform: scale(1.1);
                }
                .color-swatch.out-of-stock {
                    cursor: not-allowed !important;
                    position: relative;
                    /* Subtle unavailable effect */
                    filter: grayscale(0.4) saturate(0.85) brightness(0.96) blur(0.3px);
                    box-shadow: inset 0 0 0 9999px rgba(90, 90, 90, 0.06);
                }
                .unavailable-overlay {
                    display: none !important;
                }
                .unavailable-overlay.full { 
                    display: none !important; 
                }
                .color-swatch.unavailable-combination {
                    cursor: not-allowed !important;
                    position: relative !important;
                    transform: none !important; /* avoid scale pushing lines outside */
                    /* Subtle unavailable effect */
                    filter: grayscale(0.4) saturate(0.85) brightness(0.96) blur(0.3px) !important;
                    box-shadow: inset 0 0 0 9999px rgba(90, 90, 90, 0.06) !important;
                }
                .color-swatch.unavailable-combination::before,
                .color-swatch.unavailable-combination::after,
                .color-swatch.out-of-stock::before,
                .color-swatch.out-of-stock::after {
                    content: '' !important;
                    position: absolute !important;
                    left: 50% !important;
                    top: 50% !important;
                    width: calc(100% - 14px) !important; /* tiny inset adjustment */
                    height: 1px !important;
                    background-color: rgba(90, 90, 90, 0.9) !important;
                    transform-origin: center !important;
                    pointer-events: none !important;
                    z-index: 2 !important;
                }
                .color-swatch.unavailable-combination::before,
                .color-swatch.out-of-stock::before { transform: translate(-50%, -50%) rotate(45deg) !important; }
                .color-swatch.unavailable-combination::after,
                .color-swatch.out-of-stock::after { transform: translate(-50%, -50%) rotate(-45deg) !important; }
                .quantity-selector {
                    display: flex;
                    align-items: center;
                    margin-bottom: 0.8rem;
                    gap: 8px;
                }
                .quantity-controls {
                    display: flex;
                    align-items: center;
                    border: 2px solid #e2e8f0;
                    border-radius: 6px;
                    overflow: hidden;
                    background: white;
                }
                .quantity-btn {
                    background: #f8f9fa;
                    border: none;
                    padding: 6px 10px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: bold;
                    color: #495057;
                    transition: all 0.2s ease;
                    min-width: 35px;
                }
                .quantity-btn:hover {
                    background: #e9ecef;
                }
                .quantity-btn:disabled {
                    background: #f8f9fa;
                    color: #adb5bd;
                    cursor: not-allowed;
                }
                .quantity-input {
                    border: none;
                    text-align: center;
                    width: 50px;
                    padding: 6px;
                    font-size: 14px;
                    font-weight: 600;
                    background: white;
                }
                .quantity-input:focus {
                    outline: none;
                }
                .action-buttons {
                    display: flex;
                    gap: 8px;
                    margin-top: 12px;
                }
                .quantity-info {
                    font-size: 12px;
                    color: #6c757d;
                    font-style: italic;
                }
                .quantity-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .quantity-input {
                    width: 50px;
                    height: 36px;
                    text-align: center;
                    border-top: 1px solid #e2e8f0;
                    border-bottom: 1px solid #e2e8f0;
                    border-left: none;
                    border-right: none;
                    font-size: 1rem;
                }
                .action-buttons {
                    display: flex;
                    gap: 8px;
                    margin-top: 1rem;
                }
                .add-to-cart-btn {
                    background: linear-gradient(135deg, #3498db, #2980b9);
                    color: white;
                    border: none;
                    padding: 0.8rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-grow: 1;
                    justify-content: center;
                    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    font-size: 14px;
                }
                .add-to-cart-btn:hover {
                    background: linear-gradient(135deg, #2980b9, #1a5276);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(52, 152, 219, 0.4);
                }
                .add-to-cart-btn:disabled {
                    background: linear-gradient(135deg, #a0aec0, #718096);
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                .wishlist-btn {
                    background: white;
                    color: #e53e3e;
                    border: 2px solid #e53e3e;
                    padding: 0.8rem;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    width: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .wishlist-btn:hover {
                    background: #fff5f5;
                }
                .nav-pills .nav-link {
                    color: #4a5568;
                    font-weight: 500;
                    border-radius: 0;
                    border-bottom: 3px solid transparent;
                    background: none;
                    padding: 0.5rem 1rem;
                }
                .nav-pills .nav-link.active {
                    color: #3498db;
                    background: none;
                    border-bottom: 3px solid #3498db;
                }
                .review-card {
                    border: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    margin-bottom: 1rem;
                }
                .related-product-card {
                    border: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .related-product-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 6px 12px rgba(0,0,0,0.1);
                }
                .product-detail-section {
                    background: #fff;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    margin-bottom: 20px;
                }
                .product-header {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .product-gallery {
                    flex: 1;
                    min-width: 0;
                }
                .product-info {
                    flex: 1;
                    min-width: 0;
                }
                .tab-content-section {
                    background: #fff;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .shipping-info {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    color: #4a5568;
                    font-size: 0.9rem;
                    margin-top: 0.5rem;
                }
                .shipping-free {
                    color: #2ecc71;
                    font-weight: 500;
                }
                @media (max-width: 991.98px) {
                    .product-header {
                        flex-direction: column;
                        gap: 12px;
                    }
                    .main-product-image {
                        height: 300px;
                    }
                    .thumbnail-image {
                        width: 45px;
                        height: 45px;
                    }
                    .product-title {
                        font-size: 1.2rem;
                    }
                    .current-price {
                        font-size: 1.6rem;
                    }
                    .variant-buttons {
                        gap: 4px;
                    }
                    .variant-btn {
                        padding: 6px 10px;
                        font-size: 12px;
                        min-width: 40px;
                    }
                    .color-swatch {
                        width: 26px;
                        height: 26px;
                        margin-right: 4px;
                        margin-bottom: 4px;
                    }
                }
                @media (max-width: 767.98px) {
                    .product-header {
                        flex-direction: column;
                        gap: 16px;
                    }
                    .product-gallery {
                        order: 1;
                        width: 100%;
                    }
                    .product-info {
                        order: 2;
                        width: 100%;
                    }
                    .main-product-image {
                        height: 280px;
                    }
                    .image-container {
                        padding: 8px;
                    }
                    .thumbnails-container {
                        justify-content: center;
                        gap: 6px;
                    }
                    .thumbnail-image {
                        width: 45px;
                        height: 45px;
                    }
                    .product-title {
                        font-size: 1.1rem;
                        line-height: 1.3;
                        margin-bottom: 0.5rem;
                    }
                    .current-price {
                        font-size: 1.5rem;
                    }
                    .price-container {
                        margin-bottom: 0.8rem;
                    }
                    .shipping-info {
                        font-size: 0.85rem;
                        margin-top: 0.3rem;
                    }
                    .specs-table {
                        display: none;
                    }
                    .product-details-mobile {
                        display: block;
                        margin-bottom: 0.5rem;
                    }
                    .product-container {
                        padding: 0 12px !important;
                        margin: 0 !important;
                    }
                    .product-detail-section {
                        padding: 12px;
                        margin: 0 -12px;
                        border-radius: 0;
                    }
                    .product-info-section {
                        padding: 12px;
                    }
                    .variant-options {
                        margin-bottom: 0.8rem;
                    }
                    .variant-label {
                        font-size: 13px;
                        margin-bottom: 6px;
                        font-weight: 600;
                    }
                    .variant-buttons {
                        gap: 6px;
                        margin-bottom: 6px;
                        flex-wrap: wrap;
                    }
                    .variant-btn {
                        padding: 8px 14px;
                        font-size: 13px;
                        min-width: 50px;
                        margin-right: 4px;
                        margin-bottom: 4px;
                    }
                    .color-swatch {
                        width: 36px;
                        height: 36px;
                        margin-right: 6px;
                        margin-bottom: 6px;
                    }
                    .quantity-selector {
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 0.8rem;
                    }
                    .quantity-controls {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                    .action-buttons {
                        flex-direction: column;
                        gap: 10px;
                        margin-top: 1rem;
                    }
                    .action-buttons button {
                        flex: 1;
                        padding: 14px;
                        font-size: 15px;
                        border-radius: 10px;
                    }
                    .add-to-cart-btn {
                        width: 100%;
                    }
                    .wishlist-btn {
                        width: 100%;
                    }
                }
                @media (max-width: 480px) {
                    .main-product-image {
                        height: 220px;
                    }
                    .thumbnail-image {
                        width: 38px;
                        height: 38px;
                    }
                    .product-title {
                        font-size: 1rem;
                        margin-bottom: 0.4rem;
                    }
                    .current-price {
                        font-size: 1.3rem;
                    }
                    .product-info-section {
                        padding: 10px;
                    }
                    .variant-options {
                        margin-bottom: 0.6rem;
                    }
                    .variant-label {
                        font-size: 12px;
                        margin-bottom: 4px;
                    }
                    .variant-btn {
                        padding: 6px 12px;
                        font-size: 12px;
                        min-width: 40px;
                        margin-right: 4px;
                        margin-bottom: 4px;
                    }
                    .color-swatch {
                        width: 32px;
                        height: 32px;
                        margin-right: 4px;
                        margin-bottom: 4px;
                    }
                    .quantity-selector {
                        margin-bottom: 0.6rem;
                    }
                    .action-buttons {
                        margin-top: 0.8rem;
                        gap: 8px;
                    }
                    .action-buttons button {
                        padding: 12px;
                        font-size: 14px;
                    }
                    .add-to-cart-btn {
                        width: 100%;
                    }
                    .wishlist-btn {
                        width: 100%;
                    }
                    .variant-btn.out-of-stock::before,
                    .variant-btn.out-of-stock::after,
                    .variant-btn.unavailable-combination::before,
                    .variant-btn.unavailable-combination::after,
                    .color-swatch.unavailable-combination::before,
                    .color-swatch.unavailable-combination::after,
                    .color-swatch.out-of-stock::before,
                    .color-swatch.out-of-stock::after {
                        height: 1px;
                    }
                }
                
                /* Share Buttons Styles */
                .share-buttons-container {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                    margin-top: 1rem;
                }
                
                .share-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 20px;
                    border-radius: 8px;
                    text-decoration: none;
                    font-weight: 500;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    border: none;
                    cursor: pointer;
                    min-width: 120px;
                    justify-content: center;
                }
                
                .share-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    text-decoration: none;
                }
                
                .share-btn.facebook {
                    background: linear-gradient(135deg, #1877f2, #42a5f5);
                    color: white;
                }
                
                .share-btn.twitter {
                    background: linear-gradient(135deg, #1da1f2, #42a5f5);
                    color: white;
                }
                
                .share-btn.whatsapp {
                    background: linear-gradient(135deg, #25d366, #4caf50);
                    color: white;
                }
                
                .share-btn.email {
                    background: linear-gradient(135deg, #6c757d, #495057);
                    color: white;
                }
                
                .share-btn.copy-link {
                    background: linear-gradient(135deg, #6f42c1, #8e44ad);
                    color: white;
                }
                
                .share-btn i {
                    font-size: 16px;
                }
                
                /* Related Products Styles - Enhanced */
                .related-products-section {
                    background: #fff;
                    border-radius: 16px;
                    padding: 40px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e5e7eb;
                    margin-bottom: 30px;
                }
                
                .section-header {
                    margin-bottom: 40px;
                    text-align: center;
                }
                
                .section-title {
                    font-size: 28px;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0 0 8px 0;
                }
                
                .related-products-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 24px;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                .related-product-item {
                    background: #fff;
                    border-radius: 16px;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
                    transition: all 0.3s ease;
                    overflow: hidden;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    height: 380px;
                    position: relative;
                }
                
                .related-product-item:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
                    border-color: #3b82f6;
                }
                
                .product-link {
                    text-decoration: none;
                    color: inherit;
                    display: block;
                    height: 100%;
                }
                
                .product-image-container {
                    position: relative;
                    height: 180px;
                    overflow: hidden;
                    background: #f8fafc;
                    border-radius: 16px 16px 0 0;
                }
                
                .product-image {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    padding: 16px;
                    transition: transform 0.3s ease;
                }
                
                .related-product-item:hover .product-image {
                    transform: scale(1.05);
                }
                
                .discount-badge {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    color: white;
                    padding: 6px 10px;
                    border-radius: 8px;
                    font-size: 12px;
                    font-weight: 700;
                    z-index: 2;
                    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
                }
                
                .product-info {
                    padding: 20px;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    gap: 12px;
                }
                
                .product-title {
                    font-size: 18px;
                    line-height: 1.3;
                    color: #1f2937;
                    margin: 0 0 8px 0;
                    font-weight: 600;
                    height: 46px;
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    transition: color 0.2s ease;
                }
                
                .product-description {
                    font-size: 14px;
                    color: #6b7280;
                    line-height: 1.4;
                    margin: 0 0 12px 0;
                    height: 40px;
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                }
                
                .price-container {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: auto;
                }
                
                .old-price {
                    color: #9ca3af;
                    text-decoration: line-through;
                    font-size: 14px;
                    font-weight: 500;
                }
                
                .current-price {
                    color: #059669;
                    font-weight: 700;
                    font-size: 20px;
                }
                
                .no-related-products {
                    text-align: center;
                    padding: 60px 40px;
                    color: #6b7280;
                }
                
                @media (max-width: 768px) {
                    .related-products-section {
                        padding: 24px;
                    }
                    
                    .section-title {
                        font-size: 24px;
                    }
                    
                    .related-products-grid {
                        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                        gap: 20px;
                    }
                    
                    .related-product-item {
                        height: 360px;
                    }
                    
                    .product-image-container {
                        height: 160px;
                    }
                    
                    .product-image {
                        padding: 12px;
                    }
                    
                    .product-info {
                        padding: 16px;
                    }
                    
                    .product-title {
                        font-size: 16px;
                        height: 40px;
                    }
                    
                    .product-description {
                        font-size: 13px;
                        height: 36px;
                    }
                    
                    .current-price {
                        font-size: 18px;
                    }
                    
                    .share-buttons-container {
                        gap: 8px;
                    }
                    
                    .share-btn {
                        min-width: 100px;
                        padding: 10px 16px;
                        font-size: 13px;
                    }
                }
                
                @media (max-width: 480px) {
                    .related-products-grid {
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 16px;
                    }
                    
                    .related-product-item {
                        height: 320px;
                    }
                    
                    .product-image-container {
                        height: 140px;
                    }
                    
                    .product-title {
                        font-size: 14px;
                        height: 36px;
                    }
                    
                    .product-description {
                        font-size: 12px;
                        height: 32px;
                    }
                    
                    .current-price {
                        font-size: 16px;
                    }
                    
                    .share-buttons-container {
                        flex-direction: column;
                    }
                    
                    .share-btn {
                        width: 100%;
                        min-width: auto;
                    }
                }
                
                /* Mobile and Desktop Description Styles */
                .desktop-only {
                    display: block;
                }
                
                .ler-mais-btn {
                    background: #3b82f6;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                    margin-top: 15px;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
                    text-transform: none;
                    letter-spacing: 0.5px;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                
                .ler-mais-btn:hover {
                    background: #2563eb;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
                }
                
                .ler-mais-btn:active {
                    transform: translateY(0);
                }
                
                .full-description {
                    line-height: 1.6;
                    color: #374151;
                    font-size: 15px;
                }
                
                .full-description p {
                    margin-bottom: 16px;
                    white-space: pre-wrap;
                }
                
                .full-description p:last-child {
                    margin-bottom: 0;
                }
                
                .full-description p:empty {
                    display: none;
                }
                
                /* Mobile styles */
                @media (max-width: 768px) {
                    .desktop-only {
                        display: none !important;
                    }
                    
                    .mobile-search-input {
                        font-size: 16px !important;
                        min-width: 0 !important;
                        flex: 1 !important;
                    }
                    
                    .product-info {
                        padding: 20px 15px;
                    }
                    
                    .variant-options {
                        margin-bottom: 15px;
                    }
                    
                    .action-buttons {
                        margin-top: 15px;
                    }
                }
            `}</style>

            <div className="product-container bg-white p-6 rounded-lg shadow-sm" style={{ backgroundColor: '#fff' }}>
                {/* Back Arrow */}
                <div className="back-arrow-container mb-4">
                    <button 
                        onClick={() => {
                            // Go back to the previous page in browser history
                            window.history.back();
                        }}
                        className="back-arrow-btn"
                    >
                        <i className="fas fa-arrow-left"></i>
                        Atrás
                    </button>
                </div>

                {/* Product Header Section */}
                <div className="product-detail-section bg-white p-4" style={{ backgroundColor: '#fff' }}>
                    <div className="product-header">
                        {/* Product Gallery */}
                        <div className="product-gallery">
                            <div className="image-container">
                                <div 
                                    className={`image-zoom-container ${isZoomed ? 'zoomed' : ''}`}
                                    onMouseMove={handleMouseMove}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <img
                                        src={currentImage}
                                        className="main-product-image"
                                        alt={product?.title}
                                    />
                                </div>
                                <div className="thumbnails-container">
                                    {displayedGallery.slice(0, 5).map((img, index) => (
                                        <img
                                            key={index}
                                            src={img.image}
                                            className={`thumbnail-image ${currentImage === img.image ? 'active' : ''}`}
                                            onClick={() => handleThumbnailClick(img.image, index)}
                                            alt={`Thumbnail ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Product Info */}
                        <div className="product-info">
                            <h1 className="product-title">{product?.title}</h1>
                            

                            <div className="price-container">
                                {product.old_price > 0 && (
                                    <span className="original-price me-2">
                                        <PriceFormat_Basic value={product.old_price} />
                                    </span>
                                )}
                                <span className={`current-price ${priceLoading ? 'price-loading' : ''}`}>
                                    <PriceFormat_Basic value={currentPrice || product.price} />
                                </span>
                                {product.old_price > 0 && (
                                    <span className="discount-badge">
                                        {Math.round(((product.old_price - (currentPrice || product.price)) / product.old_price) * 100)}% DESCUENTO
                                    </span>
                                )}
                            </div>

                            {/* New composed variant UI (shadcn block) - keeps backend calls via handleAddToCartFromVariant */}
                            {product?.variants && product.variants.length > 0 && (
                                <div className="mt-6">
                                    <ProductVariant_01
                                        title={product.title}
                                        description={product.description}
                                        defaultImage={currentImage || product.image || product.main_image}
                                        variants={(product.variants || []).map(v => ({
                                            id: v.id ?? `${v.id}`,
                                            value: v.sku ?? v.id,
                                            label: v.label ?? (`${v.color_name || ''} ${v.size_name || ''}`.trim() || v.title || `Variant ${v.id}`),
                                            price: v.price ?? v.amount ?? product.price,
                                            salePrice: v.sale_price ?? v.discount_price,
                                            imageUrl: v.image ?? v.image_url ?? v.imageUrl,
                                            isInStock: (v.stock_qty ?? v.stock ?? product.stock_qty) > 0,
                                            availableQuantity: v.stock_qty ?? v.stock ?? product.stock_qty
                                        }))}
                                        onAddToCart={handleAddToCartFromVariant}
                                        variantLabel={product.variant_label || 'Variante'}
                                    />
                                </div>
                            )}

                            {product.shipping_ammount === 0 ? (
                                <div className="shipping-info">
                                    <i className="fas fa-truck text-success"></i>
                                    <span className="shipping-free">Envío GRATIS</span>
                                </div>
                            ) : (
                                <div className="shipping-info">
                                    <i className="fas fa-truck text-muted"></i>
                                    <span>Envío: ${product.shipping_ammount}</span>
                                </div>
                            )}



                            <div className="variant-options">
                                <label className="variant-label">Cantidad</label>
                                <div className="flex flex-col gap-2">
                                    <QuantityInputBasic
                                        quantity={quantity}
                                        onChange={setQuantity}
                                        max={product.max_cart_limit}
                                        min={1}
                                        className="w-fit"
                                    />
                                    {product.max_cart_limit > 0 && (
                                        <span className="quantity-info text-sm text-muted-foreground">
                                            Máx: {product.max_cart_limit}
                                        </span>
                                    )}
                                </div>
                            </div>


                            

                            {size.length > 0 && (
                                <div className="variant-options">
                                    <label className="variant-label">{product?.size_label || 'Tamaño'}</label>
                                    <div className="variant-buttons">
                                        {size.map((s, index) => {
                                            // Check availability
                                            const hasAnyStock = isSizeAvailable(s.name);
                                            const isComboAvailable = isVariantAvailable(selectedColor, s.name);
                                            const isSelected = selectedSize === s.name;

                                            // Always render. If no stock at all or combo unavailable, show disabled with overlay.
                                            const isDisabled = !hasAnyStock || !isComboAvailable;

                                            return (
                                            <button
                                                key={index}
                                                    className={`variant-btn ${isSelected ? 'selected' : ''} ${isDisabled ? 'unavailable-combination' : ''}`}
                                                    onClick={() => {
                                                        if (!isDisabled) {
                                                            handleSizeButtonClick(s.name);
                                                        }
                                                        // Don't allow clicking unavailable sizes
                                                    }}
                                                    disabled={isDisabled}
                                                    title={isDisabled ? (!hasAnyStock ? 'Agotado' : 'No disponible con el color seleccionado') : ''}
                                                >
                                                    <span className="variant-label-text">{s.name}</span>
                                            </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {color.length > 0 && (
                                <div className="variant-options">
                                    <label className="variant-label">Color: {selectedColor}</label>
                                    <VariantSelectorImages
                                        value={selectedColor}
                                        onValueChange={(colorName) => {
                                            const selectedColorData = color.find(c => c.name === colorName);
                                            if (selectedColorData) {
                                                handleColorButtonClick(product.id, colorName, selectedColorData.image, selectedColorData.galleries);
                                            }
                                        }}
                                        variants={color.map((c, index) => ({
                                            id: `color-${index}`,
                                            value: c.name,
                                            label: c.name,
                                            url: c.image,
                                            disabled: !isColorAvailable(c.name)
                                        }))}
                                        className="grid-cols-3 sm:grid-cols-4"
                                    />
                                </div>
                            )}

                            <table className="specs-table">
                                <tbody>
                                    <tr>
                                        <th>Disponibilidad</th>
                                        <td>{product.stock_qty > 0 ? 'En Stock' : 'Agotado'}</td>
                                    </tr>
                                    <tr>
                                        <th>Categoría</th>
                                        <td>{product?.category?.title}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className="product-details-mobile">
                                <div className="detail-item">
                                    <span className="detail-label">Disponibilidad:</span>
                                    <span className="detail-value">{product.stock_qty > 0 ? 'En Stock' : 'Agotado'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Categoría:</span>
                                    <span className="detail-value">{product?.category?.title}</span>
                                </div>
                            </div>

                            <div className="action-buttons">
                                <button
                                    className="add-to-cart-btn"
                                    onClick={handleAddToCart}
                                        disabled={false}
                                >
                                    <i className="fas fa-shopping-cart"></i>
                                        Agregar al Carrito
                                </button>
                                <button
                                    className="wishlist-btn"
                                    onClick={() => {
                                        if (!userData) {
                                            Swal.fire({
                                                icon: 'info',
                                                title: 'Inicio de Sesión Requerido',
                                                text: 'Por favor inicia sesión para agregar artículos a la lista de deseos.',
                                                confirmButtonText: 'Ir al Inicio de Sesión',
                                                showCancelButton: true,
                                                cancelButtonText: 'Cancelar'
                                            }).then((result) => {
                                                if (result.isConfirmed) {
                                                    window.location.href = '/login';
                                                }
                                            });
                                        } else {
                                            Toast.fire({
                                                icon: 'success',
                                                title: 'Agregado a la Lista de Deseos',
                                            });
                                        }
                                    }}
                                >
                                    <i className="fas fa-heart"></i>
                                </button>
                            </div>
                            
                            {/* Trust Badges */}
                        </div>
                    </div>
                </div>

                {/* Specifications Section */}
                <div className="tab-content-section">
                    <h3 className="mb-4">Especificaciones</h3>
                    
                    {specifications.length > 0 ? (
                        <div className="specifications-table">
                            {specifications.map((spec, index) => (
                                <div key={index} className="spec-row">
                                    <div className="spec-label">{spec.title}</div>
                                    <div className="spec-value">{spec.content}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center text-muted py-4">
                            <p className="mb-0">No hay especificaciones disponibles para este producto</p>
                        </div>
                    )}
                </div>

                {/* Full Description Section */}
                <div id="full-description-section" className="tab-content-section">
                    <h3 className="mb-4">Descripción</h3>
                    <div className="full-description">
                        {product?.description
                            .split('\n')
                            .filter(paragraph => paragraph.trim() !== '')
                            .map((paragraph, i) => (
                                <p key={i}>{paragraph.trim()}</p>
                            ))
                        }
                        {product?.description
                            .split('\n')
                            .filter(paragraph => paragraph.trim() !== '').length > 2 && (
                            <button 
                                className="ler-mais-btn"
                                onClick={() => {
                                    const descriptionSection = document.getElementById('full-description-section');
                                    if (descriptionSection) {
                                        descriptionSection.scrollIntoView({ 
                                            behavior: 'smooth',
                                            block: 'start',
                                            inline: 'nearest'
                                        });
                                    }
                                }}
                            >
                                <i className="fas fa-chevron-up"></i>
                                Ler menos
                            </button>
                        )}
                    </div>
                </div>

                {/* Share Section */}
                <div className="tab-content-section mt-4">
                    <h4 className="mb-4">Comparte este producto</h4>
                    <div className="share-buttons-container">
                        <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn facebook"
                            title="Compartir en Facebook"
                        >
                            <i className="fab fa-facebook-f"></i>
                            <span>Facebook</span>
                        </a>
                        <a
                            href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=Mira este producto de Super Paraguai: ${product?.title}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn twitter"
                            title="Compartir en Twitter"
                        >
                            <i className="fab fa-twitter"></i>
                            <span>Twitter</span>
                        </a>
                        <a
                            href={`https://wa.me/?text=Mira este producto de Super Paraguai: ${product?.title} - ${window.location.href}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="share-btn whatsapp"
                            title="Compartir en WhatsApp"
                        >
                            <i className="fab fa-whatsapp"></i>
                            <span>WhatsApp</span>
                        </a>
                        <a
                            href={`mailto:?subject=Mira este producto de Super Paraguai&body=Mira este producto: ${product?.title} - ${window.location.href}`}
                            className="share-btn email"
                            title="Compartir por Email"
                        >
                            <i className="fas fa-envelope"></i>
                            <span>Email</span>
                        </a>
                        <button
                            className="share-btn copy-link"
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                                Toast.fire({
                                    icon: 'success',
                                    title: '¡Enlace copiado al portapapeles!',
                                });
                            }}
                            title="Copiar enlace"
                        >
                            <i className="fas fa-link"></i>
                            <span>Copiar Enlace</span>
                        </button>
                    </div>
                </div>

                {/* Related Products Section */}
                <div className="related-products-section mt-5">
                    <div className="section-header">
                        <h2 className="section-title">Productos Relacionados</h2>
                    </div>
                    
                    {translatedRelatedProducts.length > 0 ? (
                        <div className="related-products-grid">
                            {translatedRelatedProducts.slice(0, 6).map((p, index) => (
                                <div key={index} className="related-product-item">
                                    <Link to={`/detail/${p.slug}`} className="product-link">
                                        <div className="product-image-container">
                                            <img
                                                src={p.image}
                                                alt={p.title}
                                                className="product-image"
                                            />
                                            {p.old_price > 0 && (
                                                <div className="discount-badge">
                                                    {Math.round(((p.old_price - p.price) / p.old_price) * 100)}% OFF
                                                </div>
                                            )}
                                        </div>
                                        <div className="product-info">
                                            <h4 className="product-title">{p.title}</h4>
                                            {p.description && (
                                                <p className="product-description">
                                                    {p.description.length > 80 ? p.description.substring(0, 80) + '...' : p.description}
                                                </p>
                                            )}
                                            <div className="price-container">
                                                {p.old_price > 0 && (
                                                    <span className="old-price"><PriceFormat_Basic value={p.old_price} /></span>
                                                )}
                                                <span className="current-price"><PriceFormat_Basic value={p.price} /></span>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-related-products">
                            <p className="text-muted">No se encontraron productos relacionados.</p>
                        </div>
                    )}
                </div>
            </div>
        </main>


    );
}

export default ProductDetail;